import { FileTextOutlined, PrinterOutlined } from '@ant-design/icons';
import { Button, Col, DatePicker, Form, Input, Row, Space, Table, Typography } from 'antd';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { useReactToPrint } from 'react-to-print';
import { formatAmount, rangePresets } from '../../../../common/utils/common.utils';
import { FormButton } from '../../../../components/common/FormItem/FormItems';
import { SelectAirline } from '../../../../components/common/FormItem/SelectCustomFeilds';
import CommonViewReport from '../../../../components/common/Invoice/ViewInvoiceInfo/CommonViewReport';
import BreadCrumb from '../../../../components/common/breadCrumb/BreadCrumb';
import LoadingIndicator from '../../../../components/common/spinner/LoadingIndicator';
import FormHeaderTitle from '../../../Invoice(Visa)/Components/FormHeaderTitle';
import { useLazyGetExcelQuery } from '../../ledgers/endpoints/ledgerEndpoints';
import { useLazyGetAirlineWiseSalesReportQuery } from '../endpoints/AirlineWiseSalesReportEndpoints';
import { IAirlineWiseSalesReportFormDataType } from '../types/AirlineWiseSalesReportTypes';
import {
  AirlineWiseSalesReportColumn,
  AirlineWiseSalesReportColumnDusty,
} from '../utils/AirlineWiseSalesReportColumn';

const AirlineWiseSalesReport = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const [paramData, setParamData] = useState<{
    date_range: string;
    id: number | string;
  }>();
  const [form] = Form.useForm();
  const to_date = dayjs().format('YYYY-MM-DD');

  const [fetchExcel] = useLazyGetExcelQuery();
  const [excelQuery, setExcelQuery] = useState<{
    Id: string;
    query: string;
  }>({
    Id: '',
    query: '',
  });
  const [fetchAirlineWiseSales, { data, isLoading, isFetching }] =
    useLazyGetAirlineWiseSalesReportQuery();

  let clientPriceSum: number = Number(data?.data?.total_airticket_client_price || 0);
  let purchasePriceSum: number = Number(data?.data?.total_airticket_purchase_price || 0);
  let totalProfitSum: number = Number(data?.data?.total_airticket_profit || 0);
  let totalDiscount: number = Number(data?.data?.total_airticket_discount_total || 0);
  let totalExtraFee: number = Number(data?.data?.total_airticket_extra_fee || 0);

  const [dataRange, setDateRange] = useState(`from_date=${to_date}&to_date=${to_date}`);

  const onFinish = async (values: IAirlineWiseSalesReportFormDataType) => {
    const setFromDate = dayjs(values.date_range?.[0]).format('YYYY-MM-DD');
    const setToDate = dayjs(values.date_range?.[1]).format('YYYY-MM-DD');

    const queryParams = new URLSearchParams();
    queryParams.append('from_date', setFromDate);
    queryParams.append('to_date', setToDate);

    if (values.airline_id) queryParams.append('airline_id', values.airline_id.toString());
    if (values.segment) queryParams.append('segment', values.segment.trim());
    if (values.search) queryParams.append('search', values.search.trim());

    const query = `?${queryParams.toString()}`;

    setDateRange(`from_date=${setFromDate}&to_date=${setToDate}`);

    await fetchAirlineWiseSales({
      airline_id: values.airline_id,
      query,
    });

    setExcelQuery({
      Id: values.airline_id?.toString() || '',
      query,
    });

    setParamData({
      date_range: `from_date=${setFromDate}&to_date=${setToDate}`,
      id: '',
    });
  };

  useEffect(() => {
    if (searchParams && location.search) {
      const formDate = searchParams.get('from_date');
      const toDate = searchParams.get('to_date');
      const id = searchParams.get('airline_id');
      form.setFieldValue('date_range', [dayjs(formDate), dayjs(toDate)]);
      form.setFieldValue('airline_id', Number(id));
      form.submit();
    } else {
      const date_range = `from_date=${to_date}&to_date=${to_date}`;
      form.setFieldValue('date_range', [dayjs(), dayjs()]);
      form.setFieldValue('airline_id', 'all');
      fetchAirlineWiseSales({
        airline_id: 'all',
        query: `?${date_range}`,
      });
      setExcelQuery({
        Id: 'all',
        query: `?${date_range}`,
      });
      setParamData({
        date_range: date_range,
        id: '',
      });
    }
  }, []);

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `AirTicket_Wise_Sales_Report`,
    pageStyle: '',
  });

  // @HANDLE CONTENTS
  const jsx_com = data?.data?.result?.length ? (
    <div>
      <FormHeaderTitle title=' Air Ticket Wise Sales Report' />
      <Table
        size='small'
        bordered
        columns={AirlineWiseSalesReportColumn(paramData)}
        dataSource={data?.data?.result}
        pagination={false}
        summary={(_) => {
          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={1} colSpan={9}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      Total:{' '}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}></Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  {' '}
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totalDiscount)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  {' '}
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totalExtraFee)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(clientPriceSum)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(purchasePriceSum)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totalProfitSum)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
      <FormHeaderTitle title='Airline Wise Summary' />
      <Table
        size='small'
        bordered
        columns={AirlineWiseSalesReportColumnDusty()}
        dataSource={data?.data?.dusty}
        pagination={false}
        summary={(rec) => {
          const totals = rec?.reduce(
            (acc, item) => {
              acc.total_extra_fee += parseFloat(item.total_extra_fee || 0);
              acc.total_discount += parseFloat(item.total_discount || 0);
              acc.total_client_price += parseFloat(item.total_client_price || 0);
              acc.total_purchase_price += parseFloat(item.total_purchase_price || 0);
              acc.total_profit += parseFloat(item.total_profit || 0);
              return acc;
            },
            {
              total_extra_fee: 0,
              total_discount: 0,
              total_client_price: 0,
              total_purchase_price: 0,
              total_profit: 0,
            }
          );

          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={1} colSpan={1}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      Total:{' '}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1} colSpan={1}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totals.total_extra_fee)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>

                <Table.Summary.Cell index={2}>
                  {' '}
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totals.total_discount)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  {' '}
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totals.total_client_price)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totals.total_purchase_price)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2}>
                  <div
                    style={{
                      color: 'green',
                      fontWeight: '700',
                      textAlign: 'right',
                      fontSize: 10,
                    }}
                  >
                    <Typography.Text style={{ fontSize: 12 }} strong>
                      {formatAmount(totals.total_profit)}
                    </Typography.Text>
                  </div>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
    </div>
  ) : (
    <></>
  );

  // @HANDLE PRINT WITH HEADER FOOTER
  const print_content = (
    <div hidden>
      <CommonViewReport
        children={jsx_com}
        printRef={componentRef}
        title={{ info_title: '', title: 'AirTicket_Wise_Sales_Report' }}
      />
    </div>
  );
  return (
    <>
      <BreadCrumb arrOfOption={['Reports', 'AirTicket_Wise_Sales_Report']} />
      <Space style={{ marginBottom: '1rem' }}>
        <Button type='primary' onClick={handlePrint}>
          <PrinterOutlined />
          Print
        </Button>
        <Button
          type='primary'
          onClick={() => {
            fetchExcel({
              client_id: excelQuery.Id,
              query: excelQuery.query,
              excelApiName: 'sales/airlineWiseReport',
              excelName: 'airline_wise_sales_report',
            });
          }}
        >
          <FileTextOutlined />
          Excel Report
        </Button>
      </Space>
      <Form layout='vertical' form={form} onFinish={onFinish}>
        <Row justify={'space-between'} align={'middle'}>
          <Col>
            <Row gutter={6}>
              <SelectAirline name='airline_id' label='Select Airline' size={6} showAll />

              <Col span={6} xs={24} sm={24} md={5} lg={5}>
                <Form.Item label='Segment' name='segment'>
                  <Input placeholder='Search by Segment' type='number' allowClear />
                </Form.Item>
              </Col>
              <Col span={6} xs={24} sm={24} md={5} lg={5}>
                <Form.Item label='Search' name='search'>
                  <Input placeholder='Search by ticket no./PNR' allowClear />
                </Form.Item>
              </Col>
              <Col span={6} xs={24} sm={24} md={6} lg={6}>
                <Form.Item
                  label='Date Range'
                  name='date_range'
                  rules={[{ required: true, message: ` Date Range is required!` }]}
                >
                  <DatePicker.RangePicker
                    presets={rangePresets}
                    format={'DD-MM-YYYY'}
                    allowClear={false}
                    style={{ width: '100%' }}
                  />
                </Form.Item>
              </Col>
              <Col lg={2}>
                <FormButton label='Search' icon textAlign='left' loading={isLoading} />
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>

      {(isLoading || isFetching) && <LoadingIndicator />}

      {/* //----------------//------------// @LEDGER TABLE HERE //----------------//------------// */}
      {jsx_com}

      {/*-----//---------//--------- @PRINTABLE CONTENT -----------//-----------//-----*/}
      {print_content}
    </>
  );
};

export default AirlineWiseSalesReport;
